import { batch, createSignal, For, type JSX } from 'solid-js';
import { t } from '../i18n';
import { usePagedQuery } from '../lib/paged';
import { type RepoSessionRow, useRepoSessions } from '../lib/repo-detail';
import { AuditNode } from './AuditNode';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RepoAuditFilters } from './RepoAuditFilters';
import type { RepoTabProps } from './RepoOverviewTab';
import { Skeleton } from './Skeleton';

// Repo detail → Audit (#195/#349): the repo's agent sessions as a filterable,
// paginated timeline off the dedicated `/sessions` endpoint (not the 6-item
// overview slice). Each node links into the full /audit/:session_id view.
// Paging is `lib/paged` — accumulate, sticky cursor, same-cursor refetch guard,
// and a boundary that reports loading/error only while nothing is on screen.

export function RepoAuditTab(props: RepoTabProps): JSX.Element {
  const [trigger, setTrigger] = createSignal('');
  const [outcome, setOutcome] = createSignal('');

  const filters = () => ({ trigger: trigger(), outcome: outcome() });
  const list = usePagedQuery<RepoSessionRow>((cursor) =>
    useRepoSessions(() => props.encoded, filters, cursor, props.token),
  );

  // Reset in the SAME batch as the filter set, so the query key jumps straight
  // to `cursor=null` — one fetch of the new filter's page one, never a throwaway
  // at the old cursor. `reset()` also drops the prior filter's rows.
  const applyFilter = (set: (v: string) => void, v: string): void =>
    batch(() => {
      set(v);
      list.reset();
    });

  return (
    <Panel title={t('repo_detail.audit_title')}>
      <RepoAuditFilters
        trigger={trigger()}
        outcome={outcome()}
        onTrigger={(v) => applyFilter(setTrigger, v)}
        onOutcome={(v) => applyFilter(setOutcome, v)}
      />

      <QueryBoundary
        query={list.view}
        isEmpty={(r) => r.length === 0}
        loading={<Skeleton height="14rem" />}
        empty={<div class="list-empty">{t('repo_detail.audit_empty')}</div>}
      >
        {(r) => (
          <>
            <ol class="timeline">
              <For each={r}>{(s) => <AuditNode row={s} />}</For>
            </ol>
            <LoadMore list={list} label={t('repo_detail.audit_load_more')} />
          </>
        )}
      </QueryBoundary>
    </Panel>
  );
}
